import { Component, computed, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatRadioModule } from '@angular/material/radio';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { take } from 'rxjs/operators';
import { Helpers } from '../../../utilities/helpers';
import {
  CHECKED_OPTIONS,
  DataKind,
  DISPLAY_OPTIONS,
  EXPORT_OPTIONS,
  OptionDefinition,
  TABS_PER_KIND
} from './variables.catalog';
import { CurrentUserState } from '../../../state/current-user.state';
import { DataStatisticsService } from '../../../services/data-statistics.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { CustomerReportRequest } from '../../../models/api/customerReport';
import { ReportVariableCategoryDto, ReportVariableDto } from '../../../models/api/reportVariable';
import {
  CustomerCompaniesSearchRequest,
  ListingType,
  TextCriterion,
  TextMatchMode
} from '../../../models/api/customerCompaniesSearchRequest';
import { CustomerCompanySearchResultDto } from '../../../models/api/customerCompanySearchResultDto';
import { CountryFlat } from '../../../models/country';
import { IndustryFlat } from '../../../models/industry';
import { Index as StockIndexLut } from '../../../models/index';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';

type RequestMode = 'time-series' | 'static';

interface VariableSection {
  name: string;
  variables: ReportVariableDto[];
}

interface VariableTab {
  id: string;
  name: string;
  sections: VariableSection[];
}

interface DataTypeOption {
  kind: DataKind;
  title: string;
  description: string;
  icon: string;
}

const DATA_TYPE_OPTIONS: DataTypeOption[] = [
  { kind: 'company',         title: 'Company Data',     description: 'Board, financials, ESG, audit, ownership…', icon: 'business' },
  { kind: 'director',        title: 'Director Data',    description: 'Per-director rows: profile, tenure, compensation.', icon: 'groups' },
  { kind: 'voting-results',  title: 'Voting Results',   description: 'Per-ballot rows: annual and special meetings.', icon: 'how_to_vote' }
];

@Component({
  selector: 'app-data-statistics',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatRadioModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTableModule,
    MatCheckboxModule,
    MatTabsModule,
    MatChipsModule,
    MatDividerModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatSortModule,
    NgxSpinnerModule,
    ProgressiveTypeaheadDirective
  ],
  templateUrl: './data-statistics.component.html',
  styleUrls: ['./data-statistics.component.scss']
})
export class DataStatisticsComponent implements OnInit {
  @ViewChild('stepper') private stepper!: MatStepper;

  private currentUserState = inject(CurrentUserState);
  private dataStatisticsService = inject(DataStatisticsService);
  private lookupDataService = inject(LookupDataService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private router = inject(Router);
  readonly years: string[] = [...this.currentUserState.accessibleMeasurements()].reverse();
  readonly exportOptions: OptionDefinition[] = EXPORT_OPTIONS;
  readonly displayOptions: OptionDefinition[] = DISPLAY_OPTIONS;
  readonly checkedOptions: OptionDefinition[] = CHECKED_OPTIONS;
  readonly companyColumns = ['select', 'code', 'companyName', 'stockSymbol', 'countryName', 'stockExchangeName', 'sectorName'];
  readonly dataTypeOptions = DATA_TYPE_OPTIONS;

  // The "checked" export options expose internal process-indication state, so the
  // whole section is hidden for non-internal customers (the backend also rejects them).
  readonly isInternalCustomer = computed(() => this.currentUserState.user()?.isInternal === true);

  // Cap chip rendering in the review step — instantiating a Material chip per
  // selection is expensive at 5-figure counts.
  readonly REVIEW_CHIP_LIMIT = 100;

  readonly textMatchModes: { value: TextMatchMode; label: string }[] = [
    { value: TextMatchMode.Equals,         label: 'Equals' },
    { value: TextMatchMode.StartsWith,     label: 'Starts with' },
    { value: TextMatchMode.Contains,       label: 'Contains' },
    { value: TextMatchMode.DoesNotContain, label: 'Does not contain' }
  ];

  readonly listingTypes: { value: ListingType; label: string }[] = [
    { value: ListingType.All,       label: 'All' },
    { value: ListingType.Primary,   label: 'Primary listed' },
    { value: ListingType.Secondary, label: 'Secondary listed' }
  ];

  modeForm: FormGroup;
  yearsForm: FormGroup;
  companiesForm: FormGroup;
  dataTypeForm: FormGroup;
  variablesForm: FormGroup;
  optionsForm: FormGroup;

  // Company search state — wired to POST /api/companies/customer-search.
  companySearch = signal({
    companyName: '',
    companyNameMode: TextMatchMode.Equals as TextMatchMode,
    countryId: null as string | null,
    listingType: ListingType.Primary as ListingType | null,
    industryIds: [] as string[],
    superSectorIds: [] as string[],
    sectorIds: [] as string[],
    subSectorIds: [] as string[],
    stockExchangeIds: [] as string[],
    stockIndexIds: [] as string[]
  });
  companyResults = signal<CustomerCompanySearchResultDto[]>([]);
  companiesTotal = signal(0);
  companiesPageIndex = signal(0);
  companiesPageSize = signal(10);
  companiesSortBy = signal('CompanyName');
  companiesSortAsc = signal(true);
  companiesLoading = signal(false);
  hasSearched = signal(false);

  // Lookups, populated once on init from /customer/lookups/companies-search.
  countries = signal<CountryFlat[]>([]);
  allIndustries = signal<IndustryFlat[]>([]);
  stockExchanges = signal<{ id: string; name: string }[]>([]);
  stockIndices = signal<StockIndexLut[]>([]);

  // Industry tree slices — children of the currently selected parent at each level.
  topIndustries = computed(() => this.allIndustries().filter(i => !i.parentId));
  superSectorOptions = computed(() => {
    const parents = this.companySearch().industryIds;
    if (parents.length === 0) return [];
    const parentSet = new Set(parents);
    return this.allIndustries().filter(i => i.parentId && parentSet.has(i.parentId));
  });
  sectorOptions = computed(() => {
    const parents = this.companySearch().superSectorIds;
    if (parents.length === 0) return [];
    const parentSet = new Set(parents);
    return this.allIndustries().filter(i => i.parentId && parentSet.has(i.parentId));
  });
  subSectorOptions = computed(() => {
    const parents = this.companySearch().sectorIds;
    if (parents.length === 0) return [];
    const parentSet = new Set(parents);
    return this.allIndustries().filter(i => i.parentId && parentSet.has(i.parentId));
  });

  // Selection persists across pages. Keep full DTOs so step 7 can render names.
  private selectedCompanies = signal<Map<string, CustomerCompanySearchResultDto>>(new Map());
  private get selectedCompanyIds(): string[] {
    return Array.from(this.selectedCompanies().keys());
  }

  // Catalog returned by /customers/me/report-variables — filtered server-side
  // by the customer's assigned report variable categories, so we just render what arrives.
  categories = signal<ReportVariableCategoryDto[]>([]);
  isCategoriesLoading = signal(false);

  // Step 5's visible tabs are defined by TABS_PER_KIND — each tab merges one or
  // more backend categories (e.g. Board + Committees → Corporate Governance) and
  // keeps them as named sections within the tab body. Empty sources are dropped;
  // a tab is hidden when every section is empty.
  variableGroups = computed<VariableTab[]>(() => {
    const kind = this.dataTypeKind();
    if (!kind) return [];
    const byName = new Map(this.categories().map(c => [c.name, c]));
    const byTitle = new Map<string, ReportVariableDto>();
    for (const c of this.categories()) {
      for (const v of c.variables) byTitle.set(v.title.trim(), v);
    }

    // First pass: every variable id claimed by a `sectionsByTitle` tab. These
    // are removed from the default category-grouped tabs so a variable never
    // appears under two tabs — otherwise the single-tab-only rule would
    // misidentify which tab the user's selection actually belongs to.
    const claimedIds = new Set<string>();
    for (const tab of TABS_PER_KIND[kind]) {
      if (!tab.sectionsByTitle) continue;
      for (const s of tab.sectionsByTitle) {
        for (const t of s.titles) {
          const v = byTitle.get(t.trim());
          if (v) claimedIds.add(v.id);
        }
      }
    }

    return TABS_PER_KIND[kind]
      .map(tab => {
        const sections: VariableSection[] = [];
        if (tab.sectionsByTitle) {
          // Title-based override: pull matching variables from any loaded
          // category, in the order listed in the tab definition.
          for (const sectionDef of tab.sectionsByTitle) {
            const vars: ReportVariableDto[] = [];
            const seen = new Set<string>();
            for (const title of sectionDef.titles) {
              const v = byTitle.get(title.trim());
              if (v && !seen.has(v.id)) {
                vars.push(v);
                seen.add(v.id);
              }
            }
            if (vars.length > 0) sections.push({ name: sectionDef.name, variables: vars });
          }
        } else {
          for (const catName of tab.categories) {
            const cat = byName.get(catName);
            if (!cat) continue;
            const remaining = cat.variables.filter(v => !claimedIds.has(v.id));
            if (remaining.length > 0) {
              sections.push({
                name: catName,
                variables: [...remaining].sort((a, b) => a.rank - b.rank)
              });
            }
          }
        }
        return { id: tab.name, name: tab.name, sections };
      })
      .filter(t => t.sections.length > 0);
  });

  // Flattened variables across all sections of a tab — used for the
  // tab-level "Select all in tab" toggle and selection check.
  private variablesInTab(tab: VariableTab): ReportVariableDto[] {
    return tab.sections.flatMap(s => s.variables);
  }

  // Per-bucket tab counts — drives step-4 card availability and the tooltip
  // explaining why a card is disabled.
  availabilityByKind = computed<Record<DataKind, number>>(() => {
    const byName = new Map(this.categories().map(c => [c.name, c]));
    const titles = new Set<string>();
    for (const c of this.categories()) {
      for (const v of c.variables) titles.add(v.title.trim());
    }
    const result = { company: 0, director: 0, 'voting-results': 0 } as Record<DataKind, number>;
    for (const kind of Object.keys(TABS_PER_KIND) as DataKind[]) {
      result[kind] = TABS_PER_KIND[kind].filter(tab => {
        if (tab.sectionsByTitle) {
          return tab.sectionsByTitle.some(s => s.titles.some(t => titles.has(t.trim())));
        }
        return tab.categories.some(name => (byName.get(name)?.variables.length ?? 0) > 0);
      }).length;
    }
    return result;
  });

  // True while an export request is in flight.
  exportRunning = signal(false);

  private dataTypeKind = signal<DataKind | null>(null);
  private modeKind = signal<RequestMode | null>(null);

  // Ref-counted spinner — parallel API calls show once and only hide when
  // the last one finishes. ngx-spinner.hide() is a flat toggle, not a counter.
  private spinnerRefs = 0;
  private spinnerShow(): void {
    if (this.spinnerRefs++ === 0) this.spinnerService.show();
  }
  private spinnerHide(): void {
    this.spinnerRefs = Math.max(0, this.spinnerRefs - 1);
    if (this.spinnerRefs === 0) this.spinnerService.hide();
  }

  // Variable id -> { categoryName, variable }; categoryName surfaces the step-5
  // tab name (e.g. "Corporate Governance") rather than the backend category
  // ("Board"/"Committees") so the review summary stays consistent with the UI.
  // For variables placed via `sectionsByTitle` we use the rendered tab+section
  // so the review label matches what the user actually saw in step 5.
  private variableLookup = computed<Map<string, { categoryName: string; variable: ReportVariableDto }>>(() => {
    const kind = this.dataTypeKind();
    const tabByCategory = new Map<string, string>();
    if (kind) {
      for (const tab of TABS_PER_KIND[kind]) {
        for (const cat of tab.categories) tabByCategory.set(cat, tab.name);
      }
    }
    const tabByVariableId = new Map<string, string>();
    for (const tab of this.variableGroups()) {
      for (const section of tab.sections) {
        for (const v of section.variables) tabByVariableId.set(v.id, tab.name);
      }
    }
    const map = new Map<string, { categoryName: string; variable: ReportVariableDto }>();
    for (const c of this.categories()) {
      for (const v of c.variables) {
        const tabName = tabByVariableId.get(v.id) ?? tabByCategory.get(c.name) ?? c.name;
        map.set(v.id, { categoryName: tabName, variable: v });
      }
    }
    return map;
  });

  constructor(private fb: FormBuilder) {
    this.modeForm = this.fb.group({
      mode: this.fb.control<RequestMode | null>(null, Validators.required)
    });

    this.yearsForm = this.fb.group({
      yearFrom: this.fb.control<string | null>(null),
      yearTo: this.fb.control<string | null>(null),
      year: this.fb.control<string | null>(null)
    });

    this.companiesForm = this.fb.group({
      selectedCompanyIds: this.fb.control<string[]>([], minSelectionValidator(1))
    });

    // Mirror the selected-companies map into the form control so stepper gating works.
    effect(() => {
      const ids = Array.from(this.selectedCompanies().keys());
      this.companiesForm.get('selectedCompanyIds')!.setValue(ids, { emitEvent: false });
      this.companiesForm.get('selectedCompanyIds')!.updateValueAndValidity({ emitEvent: false });
    });

    this.dataTypeForm = this.fb.group({
      dataType: this.fb.control<DataKind | null>(null, Validators.required)
    });

    this.variablesForm = this.fb.group({
      selected: this.fb.control<string[]>([], minSelectionValidator(1))
    });

    this.optionsForm = this.fb.group(
      Object.fromEntries(this.exportOptions.map(opt => [opt.id, this.fb.control(false)]))
    );
  }

  ngOnInit(): void {
    // Mode drives validators on the years step. Re-apply on every change.
    this.modeForm.get('mode')!.valueChanges.subscribe((mode: RequestMode | null) => {
      this.modeKind.set(mode);
      this.applyYearsValidators(mode);
    });

    // Data-type change clears the variable selection (catalog changes).
    this.dataTypeForm.get('dataType')!.valueChanges.subscribe((kind: DataKind | null) => {
      this.dataTypeKind.set(kind);
      this.variablesForm.get('selected')!.setValue([]);
      this.variablesForm.get('selected')!.markAsUntouched();
    });

    this.loadCategories();
    this.loadCompanySearchLookups();
  }

  private loadCompanySearchLookups(): void {
    this.spinnerShow();
    this.lookupDataService.loadCustomerCompanySearchLookups()
      .then(() => {
        this.lookupDataService.getTableStream<CountryFlat>('countries')
          .pipe(take(1))
          .subscribe(v => this.countries.set((v ?? []).filter(c => (c as any).isVisible !== false)
            .sort((a, b) => a.name.localeCompare(b.name))));
        this.lookupDataService.getTableStream<IndustryFlat>('industries')
          .pipe(take(1))
          .subscribe(v => this.allIndustries.set((v ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))));
        this.lookupDataService.getTableStream<{ id: string; name: string }>('stockExchanges')
          .pipe(take(1))
          .subscribe(v => this.stockExchanges.set((v ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))));
        this.lookupDataService.getTableStream<StockIndexLut>('stockIndices')
          .pipe(take(1))
          .subscribe(v => this.stockIndices.set((v ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))));
      })
      .catch(err => {
        console.error('[data-statistics] failed to load companies-search lookups', err);
        this.toast.error(`<strong>Failed to load search filters:</strong></br></br> ${Helpers.parseApiError(err)}`);
      })
      .finally(() => this.spinnerHide());
  }

  private loadCategories(): void {
    this.isCategoriesLoading.set(true);
    this.spinnerShow();
    this.dataStatisticsService.getReportVariables().subscribe({
      next: cats => {
        this.categories.set(cats ?? []);
        this.isCategoriesLoading.set(false);
        this.spinnerHide();
      },
      error: err => {
        console.error('[data-statistics] failed to load report variables', err);
        this.toast.error(`<strong>Failed to load variables:</strong></br></br> ${Helpers.parseApiError(err)}`);
        this.isCategoriesLoading.set(false);
        this.spinnerHide();
      }
    });
  }

  private applyYearsValidators(mode: RequestMode | null): void {
    const yearFrom = this.yearsForm.get('yearFrom')!;
    const yearTo = this.yearsForm.get('yearTo')!;
    const year = this.yearsForm.get('year')!;

    yearFrom.clearValidators();
    yearTo.clearValidators();
    year.clearValidators();

    if (mode === 'time-series') {
      yearFrom.setValidators([Validators.required]);
      yearTo.setValidators([Validators.required]);
      year.setValue(null, { emitEvent: false });
    } else if (mode === 'static') {
      year.setValidators([Validators.required]);
      yearFrom.setValue(null, { emitEvent: false });
      yearTo.setValue(null, { emitEvent: false });
      if (!year.value && this.years.length > 0) {
        year.setValue(this.years[0], { emitEvent: false });
      }
    } else {
      yearFrom.setValue(null, { emitEvent: false });
      yearTo.setValue(null, { emitEvent: false });
      year.setValue(null, { emitEvent: false });
    }

    yearFrom.updateValueAndValidity({ emitEvent: false });
    yearTo.updateValueAndValidity({ emitEvent: false });
    year.updateValueAndValidity({ emitEvent: false });
  }

  // Time-series shortcut: span every accessible measurement year.
  selectAllYears(): void {
    if (this.years.length === 0) return;
    const nums = this.years.map(y => Number(y)).sort((a, b) => a - b);
    this.yearsForm.patchValue({
      yearFrom: String(nums[0]),
      yearTo: String(nums[nums.length - 1])
    });
  }

  // ---- step 1 / 4 helpers ---------------------------------------------------

  get mode(): RequestMode | null {
    return this.modeKind();
  }

  get dataType(): DataKind | null {
    return this.dataTypeKind();
  }

  isDataKindAvailable(kind: DataKind): boolean {
    return this.availabilityByKind()[kind] > 0;
  }

  dataTypeTitle(kind: DataKind | null): string {
    if (!kind) return '—';
    return DATA_TYPE_OPTIONS.find(o => o.kind === kind)?.title ?? '—';
  }

  // ---- step 3: company search ----------------------------------------------

  patchCompanySearch(patch: Partial<ReturnType<typeof this.companySearch>>): void {
    // mat-select's (ngModelChange) can emit null during reset cycles; the signal
    // shape promises arrays for the multi-select fields, so coerce here to keep
    // `<mat-select-trigger>` access safe.
    const arrayKeys = [
      'industryIds', 'superSectorIds', 'sectorIds',
      'subSectorIds', 'stockExchangeIds', 'stockIndexIds'
    ] as const;
    const next = { ...this.companySearch(), ...patch };
    for (const key of arrayKeys) {
      if (next[key] == null) next[key] = [];
    }
    this.companySearch.set(next);
  }

  // Cascading industry-tree resets: changing a parent clears every lower level.

  onIndustryChange(industryIds: string[]): void {
    this.patchCompanySearch({ industryIds, superSectorIds: [], sectorIds: [], subSectorIds: [] });
  }
  onSuperSectorChange(superSectorIds: string[]): void {
    this.patchCompanySearch({ superSectorIds, sectorIds: [], subSectorIds: [] });
  }
  onSectorChange(sectorIds: string[]): void {
    this.patchCompanySearch({ sectorIds, subSectorIds: [] });
  }
  onSubSectorChange(subSectorIds: string[]): void {
    this.patchCompanySearch({ subSectorIds });
  }
  onStockExchangeChange(stockExchangeIds: string[]): void {
    this.patchCompanySearch({ stockExchangeIds });
  }
  onStockIndexChange(stockIndexIds: string[]): void {
    this.patchCompanySearch({ stockIndexIds });
  }

  getIndustryName(id: string): string {
    return this.allIndustries().find(i => i.id === id)?.name ?? '';
  }
  getStockExchangeName(id: string): string {
    return this.stockExchanges().find(s => s.id === id)?.name ?? '';
  }
  getStockIndexName(id: string): string {
    return this.stockIndices().find(s => s.id === id)?.name ?? '';
  }

  selectAllIndustries(): void {
    this.onIndustryChange(this.topIndustries().map(i => i.id));
  }
  deselectAllIndustries(): void {
    this.onIndustryChange([]);
  }
  selectAllSuperSectors(): void {
    this.onSuperSectorChange(this.superSectorOptions().map(s => s.id));
  }
  deselectAllSuperSectors(): void {
    this.onSuperSectorChange([]);
  }
  selectAllSectors(): void {
    this.onSectorChange(this.sectorOptions().map(s => s.id));
  }
  deselectAllSectors(): void {
    this.onSectorChange([]);
  }
  selectAllSubSectors(): void {
    this.onSubSectorChange(this.subSectorOptions().map(s => s.id));
  }
  deselectAllSubSectors(): void {
    this.onSubSectorChange([]);
  }
  selectAllStockExchanges(): void {
    this.onStockExchangeChange(this.stockExchanges().map(s => s.id));
  }
  deselectAllStockExchanges(): void {
    this.onStockExchangeChange([]);
  }
  selectAllStockIndices(): void {
    this.onStockIndexChange(this.stockIndices().map(s => s.id));
  }
  deselectAllStockIndices(): void {
    this.onStockIndexChange([]);
  }

  runCompanySearch(): void {
    if (!this.canSearchCompanies()) return;
    this.companiesPageIndex.set(0);
    this.fetchCompanies();
  }

  // When no specific country is picked ("Any"), require at least one other
  // filter beyond listing status — keeps users from running unbounded searches.
  canSearchCompanies(): boolean {
    const s = this.companySearch();
    if (s.countryId) return true;
    return (s.companyName ?? '').trim().length > 0
      || s.industryIds.length > 0
      || s.superSectorIds.length > 0
      || s.sectorIds.length > 0
      || s.subSectorIds.length > 0
      || s.stockExchangeIds.length > 0
      || s.stockIndexIds.length > 0;
  }

  // Derives the year range that scopes the company search from the step-2 form.
  // time-series → [yearFrom, yearTo]; static → both equal the selected year.
  // Returns nulls when the user hasn't filled the form yet (search then falls
  // back to currently-active companies on the backend).
  private selectedYearRange(): { yearFrom: number | null; yearTo: number | null } {
    if (this.mode === 'time-series') {
      const from = this.yearsForm.value.yearFrom;
      const to = this.yearsForm.value.yearTo;
      return {
        yearFrom: from != null ? Number(from) : null,
        yearTo: to != null ? Number(to) : null
      };
    }
    if (this.mode === 'static') {
      const y = this.yearsForm.value.year;
      const num = y != null ? Number(y) : null;
      return { yearFrom: num, yearTo: num };
    }
    return { yearFrom: null, yearTo: null };
  }

  private fetchCompanies(): void {
    const s = this.companySearch();
    const { yearFrom, yearTo } = this.selectedYearRange();
    // Each industry level maps to its own column on CompanyDetails — the backend
    // ANDs whichever are set. Cascading picks naturally narrow as the user
    // drills down, matching the admin search.
    const request: CustomerCompaniesSearchRequest = {
      companyName: textCriterionOrNull(s.companyName, s.companyNameMode),
      countryIds: s.countryId ? [s.countryId] : [],
      listingType: s.listingType,
      industryIds: s.industryIds,
      superSectorIds: s.superSectorIds,
      sectorIds: s.sectorIds,
      subSectorIds: s.subSectorIds,
      stockExchangeIds: s.stockExchangeIds,
      stockIndexIds: s.stockIndexIds,
      yearFrom,
      yearTo,
      pageNumber: this.companiesPageIndex() + 1,
      pageSize: this.companiesPageSize(),
      sortBy: this.companiesSortBy(),
      ascending: this.companiesSortAsc()
    };

    this.companiesLoading.set(true);
    this.spinnerShow();
    this.dataStatisticsService.searchCustomerCompanies(request).subscribe({
      next: response => {
        this.companyResults.set(response.data ?? []);
        this.companiesTotal.set(response.totalCount ?? 0);
        this.companiesLoading.set(false);
        this.hasSearched.set(true);
        this.spinnerHide();
      },
      error: err => {
        console.error('[data-statistics] company search failed', err);
        this.toast.error(`<strong>Company search failed:</strong></br></br> ${Helpers.parseApiError(err)}`);
        this.companyResults.set([]);
        this.companiesTotal.set(0);
        this.companiesLoading.set(false);
        this.hasSearched.set(true);
        this.spinnerHide();
      }
    });
  }

  clearCompanySearch(): void {
    this.companySearch.set({
      companyName: '',
      companyNameMode: TextMatchMode.Equals,
      countryId: null,
      listingType: ListingType.Primary,
      industryIds: [],
      superSectorIds: [],
      sectorIds: [],
      subSectorIds: [],
      stockExchangeIds: [],
      stockIndexIds: []
    });
    this.companyResults.set([]);
    this.companiesTotal.set(0);
    this.companiesPageIndex.set(0);
    this.hasSearched.set(false);
  }

  onCompaniesPageChange(event: PageEvent): void {
    this.companiesPageIndex.set(event.pageIndex);
    this.companiesPageSize.set(event.pageSize);
    this.fetchCompanies();
  }

  onCompaniesSortChange(sort: Sort): void {
    if (!sort.direction) {
      this.companiesSortBy.set('CompanyName');
      this.companiesSortAsc.set(true);
    } else {
      this.companiesSortBy.set(toBackendSortKey(sort.active));
      this.companiesSortAsc.set(sort.direction === 'asc');
    }
    this.fetchCompanies();
  }

  toggleCompany(company: CustomerCompanySearchResultDto): void {
    const map = new Map(this.selectedCompanies());
    if (map.has(company.id)) map.delete(company.id);
    else map.set(company.id, company);
    this.selectedCompanies.set(map);
  }

  isCompanySelected(id: string): boolean {
    return this.selectedCompanies().has(id);
  }

  toggleAllVisibleCompanies(): void {
    const visible = this.companyResults();
    const map = new Map(this.selectedCompanies());
    const allSelected = visible.length > 0 && visible.every(co => map.has(co.id));
    if (allSelected) visible.forEach(co => map.delete(co.id));
    else visible.forEach(co => map.set(co.id, co));
    this.selectedCompanies.set(map);
  }

  areAllVisibleCompaniesSelected(): boolean {
    const visible = this.companyResults();
    const map = this.selectedCompanies();
    return visible.length > 0 && visible.every(co => map.has(co.id));
  }

  areSomeVisibleCompaniesSelected(): boolean {
    const visible = this.companyResults();
    const map = this.selectedCompanies();
    return visible.some(co => map.has(co.id)) && !this.areAllVisibleCompaniesSelected();
  }

  selectedCompanyCount(): number {
    return this.selectedCompanies().size;
  }

  clearSelectedCompanies(): void {
    this.selectedCompanies.set(new Map());
  }

  /**
   * Fetches every company matching the current filters and replaces the
   * selection with the result. Because results are server-paged we can't just
   * toggle visible rows — this calls a companion unpaged endpoint (capped at
   * 5000 server-side).
   */
  selectAllMatching(): void {
    if (this.companiesTotal() === 0) return;

    const s = this.companySearch();
    const { yearFrom, yearTo } = this.selectedYearRange();
    const request: CustomerCompaniesSearchRequest = {
      companyName: textCriterionOrNull(s.companyName, s.companyNameMode),
      countryIds: s.countryId ? [s.countryId] : [],
      listingType: s.listingType,
      industryIds: s.industryIds,
      superSectorIds: s.superSectorIds,
      sectorIds: s.sectorIds,
      subSectorIds: s.subSectorIds,
      stockExchangeIds: s.stockExchangeIds,
      stockIndexIds: s.stockIndexIds,
      yearFrom,
      yearTo,
      pageNumber: 1,
      pageSize: 1,
      sortBy: this.companiesSortBy(),
      ascending: this.companiesSortAsc()
    };

    this.spinnerShow();
    this.dataStatisticsService.searchAllCustomerCompanies(request).subscribe({
      next: companies => {
        const map = new Map<string, CustomerCompanySearchResultDto>();
        companies.forEach(c => map.set(c.id, c));
        this.selectedCompanies.set(map);
        this.spinnerHide();
      },
      error: err => {
        console.error('[data-statistics] select-all-matching failed', err);
        this.toast.error(`<strong>Could not select all matching:</strong></br></br> ${Helpers.parseApiError(err)}`);
        this.spinnerHide();
      }
    });
  }

  // computed → memoized between template reads; sort runs once per change.
  selectedCompaniesList = computed<CustomerCompanySearchResultDto[]>(() =>
    Array.from(this.selectedCompanies().values())
      .sort((a, b) => (a.companyName ?? '').localeCompare(b.companyName ?? ''))
  );

  // ---- step 5: variable selection ------------------------------------------

  isVariableSelected(id: string): boolean {
    return (this.variablesForm.get('selected')!.value as string[]).includes(id);
  }

  // Single-tab-only rule: while any variable is selected, only its tab is
  // active — checkboxes and "Select all" on the other tabs are disabled.
  // Returns the active tab, or null when no variables are selected.
  activeVariableTab(): VariableTab | null {
    const selected = new Set<string>(this.variablesForm.get('selected')!.value as string[]);
    if (selected.size === 0) return null;
    for (const tab of this.variableGroups()) {
      for (const section of tab.sections) {
        if (section.variables.some(v => selected.has(v.id))) return tab;
      }
    }
    return null;
  }

  clearVariableSelection(): void {
    const ctrl = this.variablesForm.get('selected')!;
    ctrl.setValue([]);
    ctrl.markAsUntouched();
  }

  toggleVariable(id: string): void {
    const ctrl = this.variablesForm.get('selected')!;
    const current = [...(ctrl.value as string[])];
    const idx = current.indexOf(id);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(id);
    ctrl.setValue(current);
    ctrl.markAsTouched();
  }

  toggleAllInGroup(tab: VariableTab): void {
    const ctrl = this.variablesForm.get('selected')!;
    const current = new Set<string>(ctrl.value as string[]);
    const variables = this.variablesInTab(tab);
    const allSelected = variables.every(v => current.has(v.id));
    variables.forEach(v => {
      if (allSelected) current.delete(v.id);
      else current.add(v.id);
    });
    ctrl.setValue(Array.from(current));
    ctrl.markAsTouched();
  }

  areAllInGroupSelected(tab: VariableTab): boolean {
    const selected = new Set<string>(this.variablesForm.get('selected')!.value as string[]);
    const variables = this.variablesInTab(tab);
    return variables.length > 0 && variables.every(v => selected.has(v.id));
  }

  selectedVariableCount(): number {
    return (this.variablesForm.get('selected')!.value as string[]).length;
  }

  // ---- step 7: review summary ----------------------------------------------

  yearsSummary(): string {
    if (this.mode === 'time-series') {
      const from = this.yearsForm.value.yearFrom;
      const to = this.yearsForm.value.yearTo;
      return from && to ? `${from} – ${to}` : '—';
    }
    if (this.mode === 'static') {
      const y = this.yearsForm.value.year;
      return y ? String(y) : '—';
    }
    return '—';
  }

  // Keep as a method — depends on a FormControl value (not a signal), so the
  // computed-signal memoization wouldn't track it correctly.
  selectedVariableLabels(): { group: string; label: string }[] {
    const selected = this.variablesForm.get('selected')!.value as string[];
    const lookup = this.variableLookup();
    return selected
      .map(id => lookup.get(id))
      .filter((entry): entry is { categoryName: string; variable: ReportVariableDto } => !!entry)
      .map(entry => ({ group: entry.categoryName, label: entry.variable.title }));
  }

  enabledOptions(): OptionDefinition[] {
    return this.exportOptions.filter(opt => !!this.optionsForm.value[opt.id]);
  }

  areAllDisplayOptionsSelected(): boolean {
    return this.displayOptions.every(opt => !!this.optionsForm.value[opt.id]);
  }

  toggleAllDisplayOptions(): void {
    const target = !this.areAllDisplayOptionsSelected();
    const patch = Object.fromEntries(this.displayOptions.map(opt => [opt.id, target]));
    this.optionsForm.patchValue(patch);
  }

  // ---- export --------------------------------------------------------------

  private buildRequest(): CustomerReportRequest {
    const fromYear = this.mode === 'time-series'
      ? Number(this.yearsForm.value.yearFrom)
      : Number(this.yearsForm.value.year);
    const toYear = this.mode === 'time-series'
      ? Number(this.yearsForm.value.yearTo)
      : Number(this.yearsForm.value.year);

    // "checked" columns are internal-only — drop them defensively even though the
    // section is hidden, so a stale form value can never leak into the request.
    const checkedIds = new Set(this.checkedOptions.map(o => o.id));
    const columnOptions = this.enabledOptions()
      .map(o => o.id)
      .filter(id => this.isInternalCustomer() || !checkedIds.has(id));

    return {
      fromYear,
      toYear,
      companyIds: this.selectedCompanyIds,
      variableIds: this.variablesForm.value.selected as string[],
      columnOptions,
      groupName: this.activeVariableTab()?.name
    };
  }

  exportReport(): void {
    const kind = this.dataType;
    if (!kind) return;

    const request = this.buildRequest();
    this.exportRunning.set(true);
    this.spinnerShow();

    // The backend generates the CSV and returns it as a file; the browser only
    // ever holds the finished ~file-sized blob, never the full dataset.
    const observable$ = kind === 'company'
      ? this.dataStatisticsService.generateCompanyReport(request)
      : kind === 'director'
        ? this.dataStatisticsService.generateDirectorReport(request)
        : this.dataStatisticsService.generateVotingResultsReport(request);

    observable$.subscribe({
      next: csv => {
        triggerCsvDownload(csv, this.buildCsvFilename(kind));
        this.exportRunning.set(false);
        this.spinnerHide();
      },
      error: err => {
        console.error('[data-statistics] export failed', err);
        this.exportRunning.set(false);
        this.spinnerHide();
        parseBlobError(err).then(message =>
          this.toast.error(`<strong>Report generation failed:</strong></br></br> ${message}`));
      }
    });
  }

  back(): void {
    this.router.navigate(['/portal']);
  }

  // ---- reset ---------------------------------------------------------------

  // Clears every step's state and returns the stepper to step 1. Used by the
  // "Start new search" button so the user can build a fresh report without
  // reloading the page.
  resetAll(): void {
    // MatStepper.reset() calls stepControl.reset() with no args on every step,
    // which nulls array-typed controls (selected, selectedCompanyIds). Run it
    // first, then explicit resets restore the proper [] defaults.
    this.stepper?.reset();

    this.modeForm.reset({ mode: null });
    this.yearsForm.reset({ yearFrom: null, yearTo: null, year: null });
    this.dataTypeForm.reset({ dataType: null });
    this.variablesForm.reset({ selected: [] });
    this.optionsForm.reset(
      Object.fromEntries(this.exportOptions.map(opt => [opt.id, false]))
    );

    this.clearCompanySearch();
    this.selectedCompanies.set(new Map());
    this.companiesPageSize.set(10);
    this.companiesSortBy.set('CompanyName');
    this.companiesSortAsc.set(true);

    this.exportRunning.set(false);
  }

  // ---- CSV download --------------------------------------------------------

  private buildCsvFilename(kind: DataKind): string {
    const yearPart = this.mode === 'time-series'
      ? `${this.yearsForm.value.yearFrom}-${this.yearsForm.value.yearTo}`
      : `${this.yearsForm.value.year}`;
    const kindSlug = kind === 'voting-results' ? 'voting-results' : kind;
    const groupSlug = slugifyGroupName(this.activeVariableTab()?.name);
    const groupPart = groupSlug ? `_${groupSlug}` : '';
    return `data-for-statistics_${kindSlug}${groupPart}_${yearPart}.csv`;
  }
}

// Turns a tab display name like "Corporate Governance" into the lowercase,
// hyphen-separated form used in the CSV filename.
function slugifyGroupName(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function minSelectionValidator(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (Array.isArray(value) && value.length >= min) return null;
    return { minSelection: { required: min, actual: Array.isArray(value) ? value.length : 0 } };
  };
}

function textCriterionOrNull(value: string, mode: TextMatchMode): TextCriterion | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  return { mode, value: trimmed };
}

// Map mat-table column propertyNames to the keys the backend's ApplySorting
// switch matches against (it ToLower()s the input).
function toBackendSortKey(active: string): string {
  switch (active) {
    case 'code':          return 'Code';
    case 'companyName':   return 'CompanyName';
    case 'stockSymbol':   return 'StockSymbol';
    case 'countryName':   return 'CountryName';
    case 'sectorName':    return 'SectorName';
    default:              return 'CompanyName';
  }
}

// Reads the JSON error body of a failed blob-typed request. With responseType
// 'blob' even error responses arrive as a Blob, so HttpErrorResponse.error
// must be read asynchronously rather than inspected directly.
async function parseBlobError(err: unknown): Promise<string> {
  const error = (err as { error?: unknown })?.error;
  if (error instanceof Blob) {
    try {
      const parsed = JSON.parse(await error.text());
      return parsed?.error ?? parsed?.message ?? 'An unexpected error occurred.';
    } catch {
      return 'An unexpected error occurred.';
    }
  }
  return Helpers.parseApiError(err);
}

// The backend already emits the CSV with a UTF-8 BOM, so the blob is saved as-is.
function triggerCsvDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

